import React from 'react';
import { Home, Plus, Calendar, Stethoscope, Trophy, Crown } from 'lucide-react';
import { useSubscription } from '../subscription/SubscriptionContext';
import SourdoughStarterIcon from './SourdoughStarterIcon.tsx';
import { shouldBypassSubscription } from '../../lib/platform';

interface NavigationProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
  layout: 'horizontal' | 'vertical';
}

export function Navigation({ activeTab, onTabChange, layout }: NavigationProps) {
  const { isSubscribed } = useSubscription();

  // Hide premium UI elements on WebViewGold iOS and Safari iOS/iPad (App Store compliance)
  const showPremiumUI = !shouldBypassSubscription() && !isSubscribed;

  const navItems = [
    { id: 'home', icon: Home, label: 'Home' },
    { id: 'starter', icon: SourdoughStarterIcon, label: 'Starter' },
    { id: 'challenges', icon: Trophy, label: 'Challenges', premium: true },
    { id: 'analysis', icon: Stethoscope, label: 'Analysis', premium: true },
  ];

  const handleNewBakeClick = () => {
    if (isSubscribed) {
      onTabChange('new');
    } else {
      // This will trigger the subscription modal in the parent component
      onTabChange('new');
    }
  };
  if (layout === 'horizontal') {
    return (
      <nav className="bg-white border-t border-gray-200 fixed bottom-0 left-0 right-0 z-50 rounded-t-3xl shadow-lg"
        style={{ boxShadow: "0 -10px 15px -3px rgba(0, 0, 0, 0.1), 0 -4px 6px -2px rgba(0, 0, 0, 0.05)" }}>
        <div className="grid grid-cols-5 items-center p-2.5">
          {/* First two nav items */}
          {navItems.slice(0, 2).map((item) => (
            <button
              key={item.id}
              onClick={() => onTabChange(item.id)}
              className={`flex flex-col items-center py-3 mb-3 ${
                activeTab === item.id ? 'text-amber-600' : 'text-gray-600'
              } ${showPremiumUI && item.premium ? 'opacity-60' : ''}`}
            >
              <div className="relative">
                <item.icon className="w-6 h-6" />
                {showPremiumUI && item.premium && (
                  <div className="absolute -top-3 -right-3 w-4 h-4 bg-amber-500 rounded-full flex items-center justify-center">
                    <Crown className="w-2.5 h-2.5 text-white fill-white" />
                  </div>
                )}
              </div>
            </button>
          ))}

          {/* Centered New Bake button */}
          <div className="flex flex-col items-center mb-3">
            <div className="relative">
              <button
                onClick={handleNewBakeClick}
                className={`w-16 h-16 bg-amber-500 hover:bg-amber-600 rounded-full flex items-center justify-center text-white shadow-lg transition-colors -mt-10 ${
                  showPremiumUI ? 'opacity-90' : ''
                }`}
              >
                <Plus className="w-8 h-8" strokeWidth="1.5"/>
              </button>
              {showPremiumUI && (
                <div className="absolute -top-1 -right-1 w-4 h-4 bg-amber-500 rounded-full flex items-center justify-center">
                  <Crown className="w-2.5 h-2.5 text-white fill-white" />
                </div>
              )}
            </div>
          </div>

          {/* Last two nav items */}
          {navItems.slice(2).map((item) => (
            <button
              key={item.id}
              onClick={() => onTabChange(item.id)}
              className={`flex flex-col items-center py-3 mb-3 ${
                activeTab === item.id ? 'text-amber-600' : 'text-gray-600'
              } ${showPremiumUI && item.premium ? 'opacity-90' : ''}`}
            >
              <div className="relative">
                <item.icon className="w-6 h-6" />
                {showPremiumUI && item.premium && (
                  <div className="absolute -top-3 -right-3 w-4 h-4 bg-amber-500 rounded-full flex items-center justify-center">
                    <Crown className="w-2.5 h-2.5 text-white fill-white" />
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>
      </nav>
    );
  }

  return (
    <nav className="h-[calc(100vh-4rem)] py-6 flex flex-col items-center fixed top-16 left-0 w-16 bg-white z-30">
      <div className="space-y-8 w-full">
        {navItems.map((item) => (
          <div key={item.id} className="flex justify-center">
            <button
              onClick={() => onTabChange(item.id)}
              className={`relative w-10 h-10 flex items-center justify-center rounded-xl transition-colors group ${
                activeTab === item.id
                  ? 'text-amber-600 bg-amber-50'
                  : 'text-gray-600 hover:text-gray-600 hover:bg-white'
              } ${showPremiumUI && item.premium ? 'opacity-90' : ''}`}
              title={item.label}
            >
              <div className="relative">
                <item.icon className="w-5 h-5" />
                {showPremiumUI && item.premium && (
                  <div className="absolute -top-3 -right-3 w-4 h-4 bg-amber-500 rounded-full flex items-center justify-center">
                    <Crown className="w-2.5 h-2.5 text-white fill-white" />
                  </div>
                )}
              </div>
            </button>
          </div>
        ))}

        <div className="flex justify-center">
          <div className="relative">
            <button
              onClick={handleNewBakeClick}
              className={`relative w-10 h-10 flex items-center justify-center text-white bg-amber-500 hover:bg-blue-600 rounded-xl transition-colors group ${
                showPremiumUI ? 'opacity-90' : ''
              }`}
              title="New Bake"
            >
              <Plus className="w-5 h-5" />
            </button>
            {showPremiumUI && (
              <div className="absolute -top-1 -right-1 w-4 h-4 bg-amber-500 rounded-full flex items-center justify-center">
                <Crown className="w-2.5 h-2.5 text-white fill-white" />
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}